import { createApi } from '@reduxjs/toolkit/query/react';
import {
  removeNode,
  insertChildNode,
  insertBeforeNode,
  insertAfterNode,
  updateNode,
  findNode,
} from 'components/organisms/folderTree/treeHelpers';
import deserialize from 'helpers/getroPropertyMapper';
import { networkBaseQueryV2 } from './queries';

const LIST_TREE_TAG = ['list-tree'];

export const listsTreeApi = createApi({
  reducerPath: 'listsTreeApi',
  baseQuery: networkBaseQueryV2,
  tagTypes: LIST_TREE_TAG,
  endpoints: (builder) => ({
    getListTree: builder.query({
      query: ({ networkId }) => ({ url: `shared_lists/tree`, networkId }),
      providesTags: LIST_TREE_TAG,
      transformResponse: (response) => response || [],
    }),
    createFolder: builder.mutation({
      query: ({ name, networkId, parentId = null }) => ({
        url: `shared_list_folders`,
        networkId,
        method: 'POST',
        data: { name, parentId },
      }),
      invalidatesTags: (result, error) => (error ? LIST_TREE_TAG : []),
      async onQueryStarted({ name, networkId, parentId = null }, { dispatch, queryFulfilled }) {
        const tempId = `tmp-${Date.now()}`;
        const patch = dispatch(
          listsTreeApi.util.updateQueryData('getListTree', { networkId }, (draft) => {
            const newFolder = { id: tempId, name, type: 'folder', children: [] };
            const updated =
              parentId == null ? [newFolder, ...draft] : insertChildNode(draft, parentId, 'folder', newFolder);
            return updated;
          }),
        );
        try {
          const { data } = await queryFulfilled;
          const realId = deserialize(data)?.id;

          if (realId != null) {
            const updateFn = (draft) => updateNode(draft, tempId, 'folder', () => ({ id: realId }));
            dispatch(listsTreeApi.util.updateQueryData('getListTree', { networkId }, updateFn));
          }
        } catch {
          patch.undo();
        }
      },
    }),
    updateFolder: builder.mutation({
      query: ({ folderId, name, networkId }) => ({
        url: `shared_list_folders/${folderId}`,
        method: 'PATCH',
        networkId,
        data: { name },
      }),
      invalidatesTags: (result, error) => (error ? LIST_TREE_TAG : []),
      async onQueryStarted({ folderId, name, networkId }, { dispatch, queryFulfilled }) {
        const updateFn = (draft) => updateNode(draft, folderId, 'folder', () => ({ name }));
        const patch = dispatch(listsTreeApi.util.updateQueryData('getListTree', { networkId }, updateFn));
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    moveFolder: builder.mutation({
      query: ({ folderId, parentId, beforeId, afterId, networkId }) => ({
        url: `shared_list_folders/${folderId}/move`,
        networkId,
        method: 'PATCH',
        data: { parentId, beforeId, afterId },
      }),
      invalidatesTags: (result, error) => (error ? LIST_TREE_TAG : []),
      async onQueryStarted({ folderId, parentId, beforeId, afterId, networkId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          listsTreeApi.util.updateQueryData('getListTree', { networkId }, (draft) => {
            const dragged = findNode(draft, folderId, 'folder');
            if (!dragged) return draft;

            const pruned = removeNode(draft, folderId, 'folder');
            let updated = pruned;
            if (beforeId != null) {
              const target = findNode(pruned, beforeId, null);
              if (target) updated = insertBeforeNode(pruned, beforeId, target.type, dragged);
            } else if (afterId != null) {
              const target = findNode(pruned, afterId, null);
              if (target) updated = insertAfterNode(pruned, afterId, target.type, dragged);
            } else {
              updated = parentId == null ? [dragged, ...pruned] : insertChildNode(pruned, parentId, 'folder', dragged);
            }
            return updated;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    moveListToFolder: builder.mutation({
      query: ({ listId, folderId, beforeId, afterId, networkId }) => ({
        url: `shared_lists/${listId}/move_to_folder`,
        networkId,
        method: 'PATCH',
        data: { parentId: folderId, beforeId, afterId },
      }),
      invalidatesTags: (result, error) => (error ? LIST_TREE_TAG : []),
      async onQueryStarted({ listId, folderId, beforeId, afterId, networkId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          listsTreeApi.util.updateQueryData('getListTree', { networkId }, (draft) => {
            const dragged = findNode(draft, listId, 'list');
            if (!dragged) return draft;

            const pruned = removeNode(draft, listId, 'list');
            let updated = pruned;
            if (beforeId != null) {
              const target = findNode(pruned, beforeId, null);
              if (target) updated = insertBeforeNode(pruned, beforeId, target.type, dragged);
            } else if (afterId != null) {
              const target = findNode(pruned, afterId, null);
              if (target) updated = insertAfterNode(pruned, afterId, target.type, dragged);
            } else {
              updated = folderId == null ? [dragged, ...pruned] : insertChildNode(pruned, folderId, 'folder', dragged);
            }
            return updated;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    deleteFolder: builder.mutation({
      query: ({ folderId, networkId }) => ({ url: `shared_list_folders/${folderId}`, method: 'DELETE', networkId }),
      invalidatesTags: (result, error) => (error ? LIST_TREE_TAG : []),
      async onQueryStarted({ folderId, networkId }, { dispatch, queryFulfilled }) {
        const updateFn = (draft) => removeNode(draft, folderId, 'folder');
        const patch = dispatch(listsTreeApi.util.updateQueryData('getListTree', { networkId }, updateFn));
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
});

export const {
  useGetListTreeQuery,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useMoveFolderMutation,
  useDeleteFolderMutation,
  useMoveListToFolderMutation,
} = listsTreeApi;
